import { LoadingOutlined } from "@ant-design/icons";
import { MdAddShoppingCart } from "react-icons/md";
import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DataContext } from "../../lib/contexts";
import {
  callEndpoint,
  getDateFromYearWeekDay,
  getStringFromYearWeekDay,
  getYearWeekDayRelative,
  intToWeekday,
} from "../../lib/functions";
import { YearWeekDay } from "../../lib/models";
import { DishPopup } from "../dishes/dishPopup";
import { DishImage } from "../dishes/dishImage";
import { AddToShoppingListPopup } from "../shopping/addToShoppingListPopup";
import * as localForage from "localforage";
import {
  Dish,
  UpdateWeekPlanEndpoint,
  WeekPlan,
} from "@ckal-software/ckal-lib/dist/apps/madplanen";
import { ActionName, dishTypeToColor } from "../../lib/lib";
import { useNotifcation } from "../../lib/hooks/useNotification";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { MIconButton } from "../elements/miconButton";
import css from "./weekView.module.css";

const dayNumbers = [0, 1, 2, 3, 4, 5, 6];
const dayActionStyle = {
  width: 32,
  height: 32,
  borderRadius: 10,
  fontSize: 18,
  color: "var(--text-color)",
};

export function WeekView() {
  const [yearWeekDay, setYearWeekDay] = useState<YearWeekDay>(
    getYearWeekDayRelative(0),
  );
  const [selectedDish, setSelectedDish] = useState<Dish>();
  const [indexOfSelectedDish, setIndexOfSelectedDish] = useState(-1);
  const [selectedDay, setSelectedDay] = useState(-1);
  const [dishIds, setDishIds] = useState<string[]>([]);
  const [isUpdatingWeekPlan, setIsUpdatingWeekPlan] = useState(false);

  const navigate = useNavigate();
  const notify = useNotifcation();

  const {
    weekPlan,
    refreshWeekPlan,
    isFetchingWeekPlan,
    dishes,
    currentAction,
    setCurrentAction,
    setWeekPlan,
  } = useContext(DataContext);

  useEffect(() => {
    const weekNumber =
      new URLSearchParams(window.location.search).get("week") || "";
    if (weekNumber) {
      setYearWeekDay(
        getYearWeekDayRelative(
          Number.parseInt(weekNumber) - getYearWeekDayRelative(0).week,
        ),
      );
    }
    localForage
      .getItem("weekViewScrollPos", (_err, val) =>
        document
          .getElementById("week-view-container")
          ?.scrollTo(0, val as number),
      )
      .then(() => localForage.setItem("weekViewScrollPos", 0));
  }, []);

  useEffect(() => {
    setSelectedDish(
      dishes.find(
        (d) =>
          d._id ===
          new URLSearchParams(window.location.search).get("selected-dish"),
      ),
    );
  }, [dishes]);

  useEffect(() => {
    if (
      JSON.stringify(yearWeekDay) ===
        JSON.stringify(getYearWeekDayRelative(0)) &&
      !(new URLSearchParams(window.location.search).get("week") || "")
    ) {
      setTimeout(
        () =>
          document.getElementById("week-view-container")?.scroll({
            top: yearWeekDay.day * 160 - 100,
            behavior: "smooth",
          }),
        50,
      );
    } else {
      document.getElementById("week-view-container")?.scrollTo(0, 0);
    }
  }, [yearWeekDay]);

  const isOutdated = useCallback(
    (dayNumber: number) =>
      getYearWeekDayRelative(0).week === yearWeekDay.week
        ? getYearWeekDayRelative(0).day > dayNumber
        : getYearWeekDayRelative(0).week > yearWeekDay.week,
    [yearWeekDay],
  );

  const weeksDishIds = useMemo(() => {
    const ids: string[] = [];
    for (let i = 0; i < 7; i++) {
      if (!isOutdated(i)) {
        ids.push(
          ...(weekPlan?.weekPlans[getStringFromYearWeekDay(yearWeekDay, i)] ||
            []),
        );
      }
    }
    return ids;
  }, [weekPlan, yearWeekDay, isOutdated]);

  async function addDishToDay(day: number, id: string) {
    const ywdString = getStringFromYearWeekDay(yearWeekDay, day);
    const newDishes = (
      weekPlan?.weekPlans[
        getStringFromYearWeekDay({ ...yearWeekDay, day: day })
      ] || []
    ).concat([id]);

    const newWeekPlan: WeekPlan = JSON.parse(JSON.stringify(weekPlan));
    newWeekPlan.weekPlans[ywdString] = newDishes;
    setWeekPlan(newWeekPlan);

    const [err] = await callEndpoint(UpdateWeekPlanEndpoint, {
      yearWeekDay: ywdString,
      dishIds: newDishes,
    });

    if (err) {
      notify("Opdatering af madplan: Der skete en fejl");
      return;
    }

    refreshWeekPlan();
  }

  const isCurrentWeek = useMemo(
    () => getYearWeekDayRelative(0).week === yearWeekDay.week,
    [yearWeekDay],
  );

  const hasTransferableIngredients = useMemo(
    () =>
      dishes
        .filter((d) => weeksDishIds.includes(d._id))
        .reduce((prev, curr) => prev + curr.ingredients.length, 0) > 0,
    [dishes, weeksDishIds],
  );

  return (
    <div className={css.wrapper}>
      <>
        {/* Top bar with week selector */}
        <div className={css.topBar}>
          <div className={css.weekSelectorWrap}>
            {[-2, -1, 0, 1, 2].map((offset) => {
              const wd = getYearWeekDayRelative(offset);
              const isActive = wd.week === yearWeekDay.week;
              const isCurrent = offset === 0;
              return (
                <div
                  key={offset}
                  className={`${css.weekPill} ${isCurrent ? css.weekPillCurrent : ""} ${isActive ? css.weekPillActive : ""}`}
                  onClick={() => setYearWeekDay(wd)}
                >
                  {wd.week}
                </div>
              );
            })}
          </div>
          {(isFetchingWeekPlan || isUpdatingWeekPlan) && (
            <LoadingOutlined className={css.loadingIcon} />
          )}
          <MIconButton
            title="Tilføj ugens ingredienser til indkøbslisten"
            icon={<MdAddShoppingCart />}
            onClick={() => {
              if (currentAction?.action === ActionName.SelectDayForDish) {
                setCurrentAction(undefined);
              }

              setDishIds(weeksDishIds);
            }}
            type={hasTransferableIngredients ? "primary" : undefined}
            style={{
              gridColumn: 3,
              gridRow: 1,
              justifySelf: "end",
              opacity: hasTransferableIngredients ? 1 : 0.4,
            }}
          />
        </div>

        {/* Days */}
        <div className={css.daysList} id="week-view-container">
          {dayNumbers.map((n) => {
            const dayDishes = (
              weekPlan?.weekPlans[getStringFromYearWeekDay(yearWeekDay, n)] ||
              []
            )
              .map((id) => dishes.find((d) => d._id === id))
              .filter((d): d is Dish => !!d);

            const outdated = isOutdated(n);
            const isCurrent = isCurrentWeek && yearWeekDay.day === n;
            const highlight =
              currentAction?.action === ActionName.SelectDayForDish;

            return (
              <div
                key={n}
                id={`day-${n}`}
                className={`${css.dayCard} ${highlight ? css.dayCardHighlight : ""} ${outdated ? css.dayCardOutdated : ""} ${outdated && dayDishes.length === 0 ? css.dayCardCompact : ""}`}
              >
                {/* Day header */}
                <div className={css.dayHeader}>
                  <div className={css.dayHeaderLeft}>
                    <span
                      className={`${css.dayName} ${isCurrent ? css.dayNameCurrent : ""}`}
                    >
                      {intToWeekday(n)}
                    </span>
                    <span
                      className={`${css.dayDate} ${isCurrent ? css.dayDateCurrent : ""}`}
                    >
                      {getDateFromYearWeekDay(yearWeekDay, n)}
                    </span>
                  </div>
                  {outdated && dayDishes.length === 0 && (
                    <span className={css.compactLabel}>Ingen planer</span>
                  )}
                  {dayDishes.length > 0 && !outdated && !highlight && (
                    <div className={css.dayActions}>
                      <MIconButton
                        title="Tilføj ret"
                        icon={<PlusOutlined />}
                        style={dayActionStyle}
                        onClick={() => {
                          localForage.setItem(
                            "weekViewScrollPos",
                            document.getElementById("week-view-container")
                              ?.scrollTop,
                          );
                          setCurrentAction({
                            action: ActionName.SelectDishForDay,
                            data: { ...yearWeekDay, day: n },
                          });
                          navigate("/catalogue");
                        }}
                      />
                      <MIconButton
                        title="Ryd dagens retter"
                        icon={<DeleteOutlined />}
                        style={dayActionStyle}
                        onClick={async () => {
                          const ywdString = getStringFromYearWeekDay(
                            yearWeekDay,
                            n,
                          );
                          const newWeekPlan: WeekPlan = JSON.parse(
                            JSON.stringify(weekPlan),
                          );
                          newWeekPlan.weekPlans[ywdString] = [];
                          setWeekPlan(newWeekPlan);

                          const [err] = await callEndpoint(
                            UpdateWeekPlanEndpoint,
                            { yearWeekDay: ywdString, dishIds: [] },
                            { setWorking: setIsUpdatingWeekPlan },
                          );

                          if (err) {
                            notify("Opdatering af madplan: Der skete en fejl");
                            return;
                          }
                          refreshWeekPlan();
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Dishes */}
                {dayDishes.map((dish, i) => (
                  <div
                    key={dish._id + i}
                    className={css.dishRow}
                    onClick={() => {
                      if (currentAction?.action === "SELECT_DAY_FOR_DISH") {
                        addDishToDay(n, currentAction.data._id);
                        setCurrentAction(undefined);
                      } else {
                        setSelectedDish(dish);
                        setSelectedDay(n);
                        setIndexOfSelectedDish(i);
                      }
                    }}
                  >
                    <DishImage
                      className={css.dishImage}
                      src={dish.imageUrl}
                      alt=""
                    />
                    <div className={css.dishInfo}>
                      <div className={css.dishName}>{dish.name}</div>
                      <div className={css.dishMeta}>
                        <span
                          className={css.dishLabel}
                          style={{
                            background: dishTypeToColor[dish.type] + "20",
                            color: dishTypeToColor[dish.type],
                          }}
                        >
                          {dish.type}
                        </span>
                        <span className={css.dishTime}>{dish.time} min</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Empty state */}
                {dayDishes.length === 0 && !outdated && (
                  <div
                    className={`${css.emptyDay} ${highlight ? css.emptyDayHighlight : ""}`}
                    onClick={() => {
                      if (currentAction?.action === "SELECT_DAY_FOR_DISH") {
                        addDishToDay(n, currentAction.data._id);
                        setCurrentAction(undefined);
                      } else {
                        localForage.setItem(
                          "weekViewScrollPos",
                          document.getElementById("week-view-container")
                            ?.scrollTop,
                        );
                        setCurrentAction({
                          action: ActionName.SelectDishForDay,
                          data: { ...yearWeekDay, day: n },
                        });
                        navigate("/catalogue");
                      }
                    }}
                  >
                    {!highlight && (
                      <>
                        <PlusOutlined />
                        Tilføj ret
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>

      {/* Popups */}
      {selectedDish && (
        <DishPopup
          dish={selectedDish}
          visible={!!selectedDish}
          onClose={() => setSelectedDish(undefined)}
          onAddToWeekPlan={() => {
            setSelectedDish(undefined);
            setCurrentAction({
              action: ActionName.SelectDayForDish,
              data: selectedDish,
            });
          }}
          folders={[]}
          onRemoveFromDay={
            isOutdated(selectedDay)
              ? undefined
              : {
                  dateString: `${intToWeekday(selectedDay).toLowerCase()} d. ${getDateFromYearWeekDay(yearWeekDay, selectedDay)}`,
                  onClick: async () => {
                    const ywdString = getStringFromYearWeekDay(
                      yearWeekDay,
                      selectedDay,
                    );
                    const newDishes = [...weekPlan!.weekPlans[ywdString]];
                    newDishes.splice(indexOfSelectedDish, 1);

                    const newWeekPlan: WeekPlan = JSON.parse(
                      JSON.stringify(weekPlan),
                    );
                    newWeekPlan.weekPlans[ywdString] = newDishes;

                    const [err] = await callEndpoint(
                      UpdateWeekPlanEndpoint,
                      { yearWeekDay: ywdString, dishIds: newDishes },
                      { setWorking: setIsUpdatingWeekPlan },
                    );

                    if (err) {
                      notify("Opdatering af madplan: Der skete en fejl");
                      return;
                    }
                    setWeekPlan(newWeekPlan);
                    setSelectedDish(undefined);
                    setIndexOfSelectedDish(-1);
                    setSelectedDay(-1);
                    await refreshWeekPlan();
                  },
                }
          }
        />
      )}
      {dishIds.length > 0 && (
        <AddToShoppingListPopup
          visible={dishIds.length > 0}
          dishIds={dishIds}
          onClose={() => setDishIds([])}
        />
      )}
    </div>
  );
}
